// src/pages/Permissions.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api from '../api/api';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  Add as AddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  ManageAccounts as ManageAccountsIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  TableChart as TableChartIcon,
  VpnKey as VpnKeyIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

/* ────────────────────────────── CONSTANTES ────────────────────────────── */

const EMPTY_PERMISSION_FORM = {
  id: null,
  name: '',
  originalName: '',
  description: '',
  role_ids: [],
  confirmCodeChange: false,
};

const EMPTY_ROLE_FORM = {
  id: null,
  name: '',
  description: '',
  permission_ids: [],
};

const PERMISSION_CODE_REGEX =
  /^[a-z0-9]+(?:-[a-z0-9]+)*(?::[a-z0-9]+(?:-[a-z0-9]+)*){1,2}$/;

/* ────────────────────────────── HELPERS ────────────────────────────── */

function normalizePermissionCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_+/g, '-')
    .replace(/-+/g, '-')
    .replace(/:+/g, ':')
    .replace(/^-+|-+$/g, '');
}

function normalizeIds(value) {
  if (!Array.isArray(value)) return [];

  return [
    ...new Set(
      value
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item > 0)
    ),
  ];
}

function getErrorMessage(error, fallback = 'Ocurrió un error inesperado.') {
  const responseMessage = error?.response?.data?.message;

  if (responseMessage) return responseMessage;

  const validationErrors = error?.response?.data?.errors;

  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((item) => item.msg || item.message)
      .filter(Boolean)
      .join(' · ');
  }

  return fallback;
}

function getRoleId(role) {
  return Number(role?.id || role?.id_role || 0);
}

function getPermissionId(permission) {
  return Number(permission?.id || 0);
}

function getPermissionModuleKey(permission) {
  return String(permission?.module_key || 'general').toLowerCase();
}

function permissionDisplayName(permission) {
  if (!permission) return '';

  return [
    permission.module_name,
    permission.resource_name,
    permission.action_name,
  ]
    .filter(Boolean)
    .join(' · ');
}

function permissionSearchText(permission) {
  return [
    permission?.name,
    permission?.description,
    permission?.module_name,
    permission?.resource_name,
    permission?.action_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('es');
}

function roleSearchText(role) {
  return [role?.name, role?.description]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('es');
}

/* ────────────────────────────── COMPONENTE ────────────────────────────── */

export default function Permissions() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const isCompactMatrix = useMediaQuery('(max-width:1280px)');

  const [activeTab, setActiveTab] = useState('matrix');

  const [matrix, setMatrix] = useState({
    roles: [],
    permissions: [],
    modules: [],
  });

  const [roles, setRoles] = useState([]);

  const [matrixLoading, setMatrixLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [matrixSearch, setMatrixSearch] = useState('');
  const [matrixModuleFilter, setMatrixModuleFilter] = useState('');

  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogModuleFilter, setCatalogModuleFilter] = useState('');

  const [rolesSearch, setRolesSearch] = useState('');

  const [pendingCells, setPendingCells] = useState(new Set());

  const [permissionDialog, setPermissionDialog] = useState({
    open: false,
    mode: 'create',
    saving: false,
    form: EMPTY_PERMISSION_FORM,
  });

  const [roleDialog, setRoleDialog] = useState({
    open: false,
    mode: 'create',
    loading: false,
    saving: false,
    form: EMPTY_ROLE_FORM,
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
    deleting: false,
    forceRequired: false,
    conflictMessage: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  /* ────────────────────────────── CARGA ────────────────────────────── */

  const loadMatrix = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setMatrixLoading(true);

      try {
        const response = await api.get('/permissions/matrix');
        const payload = response?.data || {};

        setMatrix({
          roles: Array.isArray(payload.roles) ? payload.roles : [],
          permissions: Array.isArray(payload.permissions)
            ? payload.permissions.map((permission) => ({
                ...permission,
                id: getPermissionId(permission),
                role_ids: normalizeIds(permission.role_ids),
              }))
            : [],
          modules: Array.isArray(payload.modules) ? payload.modules : [],
        });
      } catch (error) {
        console.error('[Permissions] Error cargando matriz:', error);

        notify(
          getErrorMessage(
            error,
            'No fue posible cargar la matriz de permisos.'
          ),
          'error'
        );
      } finally {
        if (!silent) setMatrixLoading(false);
      }
    },
    [notify]
  );

  const loadRoles = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setRolesLoading(true);

      try {
        const response = await api.get('/roles', {
          params: {
            page: 1,
            limit: 500,
            sort: 'name:asc',
          },
        });

        const payload = response?.data || {};

        setRoles(Array.isArray(payload.data) ? payload.data : []);
      } catch (error) {
        console.error('[Permissions] Error cargando roles:', error);

        notify(
          getErrorMessage(error, 'No fue posible cargar los perfiles.'),
          'error'
        );
      } finally {
        if (!silent) setRolesLoading(false);
      }
    },
    [notify]
  );

  const refreshEverything = useCallback(async () => {
    setRefreshing(true);

    try {
      await Promise.all([
        loadMatrix({ silent: true }),
        loadRoles({ silent: true }),
      ]);

      notify('Información actualizada correctamente.', 'success');
    } finally {
      setRefreshing(false);
    }
  }, [loadMatrix, loadRoles, notify]);

  useEffect(() => {
    loadMatrix();
    loadRoles();
  }, [loadMatrix, loadRoles]);

  /* ────────────────────────────── DATOS DERIVADOS ────────────────────────────── */

  const availableRoles = useMemo(() => {
    return roles.length > 0 ? roles : matrix.roles;
  }, [roles, matrix.roles]);

  const modules = useMemo(() => {
    if (matrix.modules.length > 0) {
      return matrix.modules;
    }

    const moduleMap = new Map();

    matrix.permissions.forEach((permission) => {
      const key = getPermissionModuleKey(permission);

      if (!moduleMap.has(key)) {
        moduleMap.set(key, {
          key,
          name: permission.module_name || key,
        });
      }
    });

    return [...moduleMap.values()].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), 'es')
    );
  }, [matrix.modules, matrix.permissions]);

  const visibleMatrixPermissions = useMemo(() => {
    const search = matrixSearch.trim().toLocaleLowerCase('es');

    return matrix.permissions.filter((permission) => {
      const matchesModule =
        !matrixModuleFilter ||
        getPermissionModuleKey(permission) === matrixModuleFilter;

      const matchesSearch =
        !search || permissionSearchText(permission).includes(search);

      return matchesModule && matchesSearch;
    });
  }, [
    matrix.permissions,
    matrixSearch,
    matrixModuleFilter,
  ]);

  const visibleCatalogPermissions = useMemo(() => {
    const search = catalogSearch.trim().toLocaleLowerCase('es');

    return matrix.permissions.filter((permission) => {
      const matchesModule =
        !catalogModuleFilter ||
        getPermissionModuleKey(permission) === catalogModuleFilter;

      const matchesSearch =
        !search || permissionSearchText(permission).includes(search);

      return matchesModule && matchesSearch;
    });
  }, [
    matrix.permissions,
    catalogSearch,
    catalogModuleFilter,
  ]);

  const visibleRoles = useMemo(() => {
    const search = rolesSearch.trim().toLocaleLowerCase('es');

    if (!search) return roles;

    return roles.filter((role) =>
      roleSearchText(role).includes(search)
    );
  }, [roles, rolesSearch]);

  const matrixStats = useMemo(() => {
    const roleCount = matrix.roles.length;
    const permissionCount = matrix.permissions.length;

    const assignedCells = matrix.permissions.reduce(
      (sum, permission) =>
        sum + normalizeIds(permission.role_ids).length,
      0
    );

    const totalCells = roleCount * permissionCount;

    return {
      roleCount,
      permissionCount,
      assignedCells,
      coverage:
        totalCells > 0
          ? Math.round((assignedCells / totalCells) * 100)
          : 0,
    };
  }, [matrix.permissions, matrix.roles]);

  /* ────────────────────────────── MATRIZ ────────────────────────────── */

  const updatePermissionRole = async (
    permissionId,
    roleId,
    enabled
  ) => {
    const cellKey = `${permissionId}:${roleId}`;

    if (pendingCells.has(cellKey)) return;

    const previousMatrix = matrix;

    setPendingCells((current) => {
      const next = new Set(current);
      next.add(cellKey);
      return next;
    });

    setMatrix((current) => ({
      ...current,
      permissions: current.permissions.map((permission) => {
        if (getPermissionId(permission) !== permissionId) {
          return permission;
        }

        const currentRoleIds = normalizeIds(permission.role_ids);

        return {
          ...permission,
          role_ids: enabled
            ? [...new Set([...currentRoleIds, roleId])]
            : currentRoleIds.filter((id) => id !== roleId),
        };
      }),
    }));

    try {
      const response = await api.patch(
        `/permissions/${permissionId}/roles/${roleId}`,
        { enabled }
      );

      const updatedPermission = response?.data?.permission;

      if (updatedPermission) {
        setMatrix((current) => ({
          ...current,
          permissions: current.permissions.map((permission) =>
            getPermissionId(permission) === permissionId
              ? {
                  ...permission,
                  ...updatedPermission,
                  role_ids: normalizeIds(updatedPermission.role_ids),
                }
              : permission
          ),
        }));
      }

      await loadRoles({ silent: true });
    } catch (error) {
      console.error('[Permissions] Error actualizando matriz:', error);

      setMatrix(previousMatrix);

      notify(
        getErrorMessage(
          error,
          'No fue posible actualizar este permiso.'
        ),
        'error'
      );
    } finally {
      setPendingCells((current) => {
        const next = new Set(current);
        next.delete(cellKey);
        return next;
      });
    }
  };

  /* ────────────────────────────── PERMISOS ────────────────────────────── */

  const openCreatePermission = () => {
    setPermissionDialog({
      open: true,
      mode: 'create',
      saving: false,
      form: {
        ...EMPTY_PERMISSION_FORM,
        role_ids: [],
      },
    });
  };

  const openEditPermission = (permission) => {
    setPermissionDialog({
      open: true,
      mode: 'edit',
      saving: false,
      form: {
        id: getPermissionId(permission),
        name: permission.name || '',
        originalName: permission.name || '',
        description: permission.description || '',
        role_ids: normalizeIds(permission.role_ids),
        confirmCodeChange: false,
      },
    });
  };

  const resetPermissionDialog = () => {
    setPermissionDialog({
      open: false,
      mode: 'create',
      saving: false,
      form: EMPTY_PERMISSION_FORM,
    });
  };

  const closePermissionDialog = () => {
    if (permissionDialog.saving) return;
    resetPermissionDialog();
  };

  const updatePermissionForm = (field, value) => {
    setPermissionDialog((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
    }));
  };

  const savePermission = async () => {
    const name = normalizePermissionCode(permissionDialog.form.name);
    const description = String(
      permissionDialog.form.description || ''
    ).trim();

    if (!name) {
      notify('Debes indicar el código del permiso.', 'warning');
      return;
    }

    if (!PERMISSION_CODE_REGEX.test(name)) {
      notify(
        'Usa el formato módulo:submódulo:acción. Ejemplo: contabilidad:plan-cuentas:ver.',
        'warning'
      );
      return;
    }

    const isEditing = permissionDialog.mode === 'edit';

    const codeChanged =
      isEditing &&
      name !== normalizePermissionCode(permissionDialog.form.originalName);

    if (codeChanged && !permissionDialog.form.confirmCodeChange) {
      notify(
        'Debes confirmar el cambio del código técnico.',
        'warning'
      );
      return;
    }

    setPermissionDialog((current) => ({
      ...current,
      saving: true,
    }));

    try {
      if (isEditing) {
        const permissionId = permissionDialog.form.id;

        const payload = {
          description: description || null,
        };

        if (codeChanged) {
          payload.name = name;
          payload.confirm_code_change = true;
        }

        await api.put(`/permissions/${permissionId}`, payload);

        await api.put(`/permissions/${permissionId}/roles`, {
          role_ids: normalizeIds(permissionDialog.form.role_ids),
        });

        notify('Permiso actualizado correctamente.', 'success');
      } else {
        await api.post('/permissions', {
          name,
          description: description || null,
          role_ids: normalizeIds(permissionDialog.form.role_ids),
        });

        notify('Permiso creado correctamente.', 'success');
      }

      resetPermissionDialog();

      await Promise.all([
        loadMatrix({ silent: true }),
        loadRoles({ silent: true }),
      ]);
    } catch (error) {
      console.error('[Permissions] Error guardando permiso:', error);

      notify(
        getErrorMessage(
          error,
          'No fue posible guardar el permiso.'
        ),
        'error'
      );

      setPermissionDialog((current) => ({
        ...current,
        saving: false,
      }));
    }
  };

  /* ────────────────────────────── ROLES ────────────────────────────── */

  const openCreateRole = () => {
    setRoleDialog({
      open: true,
      mode: 'create',
      loading: false,
      saving: false,
      form: {
        ...EMPTY_ROLE_FORM,
        permission_ids: [],
      },
    });
  };

  const openEditRole = async (role) => {
    const roleId = getRoleId(role);

    setRoleDialog({
      open: true,
      mode: 'edit',
      loading: true,
      saving: false,
      form: {
        id: roleId,
        name: role.name || '',
        description: role.description || '',
        permission_ids: [],
      },
    });

    try {
      const response = await api.get(`/roles/${roleId}`);
      const detail = response?.data || {};

      setRoleDialog({
        open: true,
        mode: 'edit',
        loading: false,
        saving: false,
        form: {
          id: getRoleId(detail),
          name: detail.name || '',
          description: detail.description || '',
          permission_ids: normalizeIds(detail.permission_ids),
        },
      });
    } catch (error) {
      console.error('[Permissions] Error cargando perfil:', error);

      notify(
        getErrorMessage(error, 'No fue posible cargar el perfil.'),
        'error'
      );

      setRoleDialog({
        open: false,
        mode: 'create',
        loading: false,
        saving: false,
        form: EMPTY_ROLE_FORM,
      });
    }
  };

  const resetRoleDialog = () => {
    setRoleDialog({
      open: false,
      mode: 'create',
      loading: false,
      saving: false,
      form: EMPTY_ROLE_FORM,
    });
  };

  const closeRoleDialog = () => {
    if (roleDialog.saving) return;
    resetRoleDialog();
  };

  const updateRoleForm = (field, value) => {
    setRoleDialog((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
    }));
  };

  const saveRole = async () => {
    const name = String(roleDialog.form.name || '')
      .trim()
      .replace(/\s+/g, ' ');

    const description = String(
      roleDialog.form.description || ''
    ).trim();

    if (!name) {
      notify('Debes indicar el nombre del perfil.', 'warning');
      return;
    }

    setRoleDialog((current) => ({
      ...current,
      saving: true,
    }));

    try {
      const permissionIds = normalizeIds(
        roleDialog.form.permission_ids
      );

      if (roleDialog.mode === 'edit') {
        const roleId = roleDialog.form.id;

        await api.put(`/roles/${roleId}`, {
          name,
          description: description || null,
        });

        await api.put(`/roles/${roleId}/permissions`, {
          permission_ids: permissionIds,
        });

        notify('Perfil actualizado correctamente.', 'success');
      } else {
        await api.post('/roles', {
          name,
          description: description || null,
          permission_ids: permissionIds,
        });

        notify('Perfil creado correctamente.', 'success');
      }

      resetRoleDialog();

      await Promise.all([
        loadMatrix({ silent: true }),
        loadRoles({ silent: true }),
      ]);
    } catch (error) {
      console.error('[Permissions] Error guardando perfil:', error);

      notify(
        getErrorMessage(error, 'No fue posible guardar el perfil.'),
        'error'
      );

      setRoleDialog((current) => ({
        ...current,
        saving: false,
      }));
    }
  };

  /* ────────────────────────────── ELIMINACIÓN ────────────────────────────── */

  const requestDeletePermission = (permission) => {
    setDeleteDialog({
      open: true,
      type: 'permission',
      item: permission,
      deleting: false,
      forceRequired: false,
      conflictMessage: '',
    });
  };

  const requestDeleteRole = (role) => {
    setDeleteDialog({
      open: true,
      type: 'role',
      item: role,
      deleting: false,
      forceRequired: false,
      conflictMessage: '',
    });
  };

  const resetDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      type: null,
      item: null,
      deleting: false,
      forceRequired: false,
      conflictMessage: '',
    });
  };

  const closeDeleteDialog = () => {
    if (deleteDialog.deleting) return;
    resetDeleteDialog();
  };

  const confirmDelete = async ({ force = false } = {}) => {
    const { type, item } = deleteDialog;

    if (!type || !item) return;

    const id =
      type === 'permission'
        ? getPermissionId(item)
        : getRoleId(item);

    if (!id) {
      notify('No fue posible identificar el registro.', 'error');
      return;
    }

    setDeleteDialog((current) => ({
      ...current,
      deleting: true,
    }));

    try {
      const endpoint =
        type === 'permission'
          ? `/permissions/${id}`
          : `/roles/${id}`;

      await api.delete(endpoint, {
        params: force ? { force: true } : undefined,
      });

      notify(
        type === 'permission'
          ? 'Permiso eliminado correctamente.'
          : 'Perfil eliminado correctamente.',
        'success'
      );

      resetDeleteDialog();

      await Promise.all([
        loadMatrix({ silent: true }),
        loadRoles({ silent: true }),
      ]);
    } catch (error) {
      console.error('[Permissions] Error eliminando:', error);

      if (error?.response?.status === 409 && !force) {
        setDeleteDialog((current) => ({
          ...current,
          deleting: false,
          forceRequired: true,
          conflictMessage: getErrorMessage(
            error,
            'Este registro tiene relaciones activas.'
          ),
        }));

        return;
      }

      notify(
        getErrorMessage(error, 'No fue posible eliminar el registro.'),
        'error'
      );

      setDeleteDialog((current) => ({
        ...current,
        deleting: false,
      }));
    }
  };

  /* ────────────────────────────── COMPONENTES VISUALES ────────────────────────────── */

  const renderMetric = (label, value, icon, tone) => {
    const palette = {
      primary: {
        bg: 'rgba(25, 118, 210, 0.10)',
        color: 'primary.main',
      },
      secondary: {
        bg: 'rgba(156, 39, 176, 0.10)',
        color: 'secondary.main',
      },
      success: {
        bg: 'rgba(46, 125, 50, 0.10)',
        color: 'success.main',
      },
      info: {
        bg: 'rgba(2, 136, 209, 0.10)',
        color: 'info.main',
      },
    };

    const selectedTone = palette[tone] || palette.primary;

    return (
      <Paper
        variant="outlined"
        sx={{
          flex: '1 1 180px',
          minWidth: 0,
          p: 1.5,
          borderRadius: 2.5,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: selectedTone.bg,
              color: selectedTone.color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>

            <Typography variant="h6" fontWeight={800} lineHeight={1.15}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const renderSearchField = (
    value,
    onChange,
    placeholder = 'Buscar…'
  ) => (
    <TextField
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      fullWidth={isMobile}
      sx={{
        width: isMobile ? '100%' : 330,
        maxWidth: '100%',
        minWidth: 0,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              onClick={() => onChange('')}
              aria-label="limpiar búsqueda"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );

  const renderModuleSelect = (value, onChange) => (
    <TextField
      select
      size="small"
      label="Módulo"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      fullWidth={isMobile}
      sx={{
        minWidth: isMobile ? '100%' : 220,
        maxWidth: '100%',
      }}
    >
      <MenuItem value="">Todos los módulos</MenuItem>

      {modules.map((module) => (
        <MenuItem key={module.key} value={module.key}>
          {module.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const permissionCodeChanged =
    permissionDialog.mode === 'edit' &&
    normalizePermissionCode(permissionDialog.form.name) !==
      normalizePermissionCode(permissionDialog.form.originalName);

  const selectedPermissionRoles = availableRoles.filter((role) =>
    normalizeIds(permissionDialog.form.role_ids).includes(
      getRoleId(role)
    )
  );

  const selectedRolePermissions = matrix.permissions.filter(
    (permission) =>
      normalizeIds(roleDialog.form.permission_ids).includes(
        getPermissionId(permission)
      )
  );

  const matrixRoleColumnWidth = isCompactMatrix ? 54 : 62;

  const moduleWidth = isCompactMatrix ? 110 : 125;
  const resourceWidth = isCompactMatrix ? 125 : 160;
  const actionWidth = isCompactMatrix ? 78 : 95;

  const resourceLeft = moduleWidth;
  const actionLeft = moduleWidth + resourceWidth;

  const matrixTableMinWidth = Math.max(
    680,
    moduleWidth +
      resourceWidth +
      actionWidth +
      matrix.roles.length * matrixRoleColumnWidth
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        boxSizing: 'border-box',
        mx: 0,
        px: { xs: 1, sm: 1.5, md: 2 },
        py: { xs: 1.25, md: 2 },
        '& > *': {
          minWidth: 0,
          maxWidth: '100%',
        },
      }}
    >
      {/* ────────────────────────────── ENCABEZADO ────────────────────────────── */}

<Paper
  elevation={0}
  sx={{
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    p: { xs: 1.75, sm: 2.25, md: 2.5 },
    mb: 2,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    background:
      'linear-gradient(135deg, rgba(25, 118, 210, 0.10), rgba(156, 39, 176, 0.04))',
  }}
>
  <Stack
    direction={{ xs: 'column', lg: 'row' }}
    alignItems={{ xs: 'stretch', lg: 'center' }}
    justifyContent="space-between"
    spacing={2}
    sx={{
      width: '100%',
      minWidth: 0,
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        flex: { lg: '1 1 auto' },
      }}
    >
      <Box
        sx={{
          width: { xs: 44, md: 52 },
          height: { xs: 44, md: 52 },
          borderRadius: 2.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
        }}
      >
        <SecurityIcon />
      </Box>

      <Box
        sx={{
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Typography
          variant="h5"
          fontWeight={900}
          sx={{
            lineHeight: 1.1,
            overflowWrap: 'anywhere',
          }}
        >
          Control de accesos
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.45,
            overflowWrap: 'anywhere',
          }}
        >
          Configura perfiles, permisos y funciones autorizadas por módulo.
        </Typography>
      </Box>
    </Stack>

    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{
        width: { xs: '100%', lg: 'auto' },
        flexShrink: 0,
      }}
    >
      <Button
        variant="outlined"
        startIcon={
          refreshing ? (
            <CircularProgress size={17} />
          ) : (
            <RefreshIcon />
          )
        }
        onClick={refreshEverything}
        disabled={refreshing}
        fullWidth={isMobile}
      >
        Actualizar
      </Button>

      {activeTab !== 'roles' ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreatePermission}
          fullWidth={isMobile}
        >
          Nuevo permiso
        </Button>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateRole}
          fullWidth={isMobile}
        >
          Nuevo perfil
        </Button>
      )}
    </Stack>
  </Stack>
</Paper>

      {/* ────────────────────────────── MÉTRICAS ────────────────────────────── */}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        mb={2}
        flexWrap="wrap"
        useFlexGap
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        {renderMetric(
          'Perfiles configurados',
          matrixStats.roleCount,
          <ManageAccountsIcon fontSize="small" />,
          'primary'
        )}

        {renderMetric(
          'Permisos registrados',
          matrixStats.permissionCount,
          <VpnKeyIcon fontSize="small" />,
          'secondary'
        )}

        {renderMetric(
          'Asignaciones activas',
          matrixStats.assignedCells,
          <CheckCircleOutlineIcon fontSize="small" />,
          'success'
        )}

        {renderMetric(
          'Cobertura de matriz',
          `${matrixStats.coverage}%`,
          <TableChartIcon fontSize="small" />,
          'info'
        )}
      </Stack>

      {/* ────────────────────────────── TABS ────────────────────────────── */}

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          minWidth: 0,
          mb: 2,
          overflow: 'hidden',
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            minHeight: 56,
            maxWidth: '100%',
            '& .MuiTab-root': {
              minHeight: 56,
              minWidth: 0,
              textTransform: 'none',
              fontWeight: 800,
            },
          }}
        >
          <Tab
            value="matrix"
            icon={<TableChartIcon fontSize="small" />}
            iconPosition="start"
            label="Matriz de accesos"
          />

          <Tab
            value="permissions"
            icon={<VpnKeyIcon fontSize="small" />}
            iconPosition="start"
            label="Catálogo de permisos"
          />

          <Tab
            value="roles"
            icon={<GroupIcon fontSize="small" />}
            iconPosition="start"
            label="Perfiles"
          />
        </Tabs>
      </Paper>

      {/* ────────────────────────────── MATRIZ ────────────────────────────── */}

      {activeTab === 'matrix' && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              minWidth: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', xl: 'center' }}
              sx={{
                minWidth: 0,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Typography variant="subtitle1" fontWeight={900}>
                  Matriz de permisos por perfil
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  Marca o desmarca una celda para otorgar o retirar un acceso.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                width={{ xs: '100%', xl: 'auto' }}
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {renderSearchField(
                  matrixSearch,
                  setMatrixSearch,
                  'Buscar permiso, módulo o función…'
                )}

                {renderModuleSelect(
                  matrixModuleFilter,
                  setMatrixModuleFilter
                )}
              </Stack>
            </Stack>
          </Box>

          {matrixLoading ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <CircularProgress />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1.5 }}
              >
                Cargando matriz de permisos…
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    maxWidth: 'none',
                    minWidth: matrixTableMinWidth,
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                  }}
                >
                  <colgroup>
                    <col style={{ width: moduleWidth }} />
                    <col style={{ width: resourceWidth }} />
                    <col style={{ width: actionWidth }} />

                    {matrix.roles.map((role) => (
                      <col
                        key={`column-role-${getRoleId(role)}`}
                        style={{ width: matrixRoleColumnWidth }}
                      />
                    ))}
                  </colgroup>

                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          top: 0,
                          zIndex: 5,
                          width: moduleWidth,
                          minWidth: moduleWidth,
                          maxWidth: moduleWidth,
                          bgcolor: 'background.paper',
                          fontWeight: 900,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Módulo
                      </TableCell>

                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: resourceLeft,
                          top: 0,
                          zIndex: 5,
                          width: resourceWidth,
                          minWidth: resourceWidth,
                          maxWidth: resourceWidth,
                          bgcolor: 'background.paper',
                          fontWeight: 900,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Submódulo
                      </TableCell>

                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: actionLeft,
                          top: 0,
                          zIndex: 5,
                          width: actionWidth,
                          minWidth: actionWidth,
                          maxWidth: actionWidth,
                          bgcolor: 'background.paper',
                          fontWeight: 900,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Acción
                      </TableCell>

                      {matrix.roles.map((role) => (
                        <TableCell
                          key={getRoleId(role)}
                          align="center"
                          sx={{
                            width: matrixRoleColumnWidth,
                            minWidth: matrixRoleColumnWidth,
                            maxWidth: matrixRoleColumnWidth,
                            px: 0.25,
                            py: 0.8,
                            height: isMobile ? 64 : 124,
                            verticalAlign: 'bottom',
                            bgcolor: 'background.paper',
                            fontWeight: 900,
                          }}
                        >
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              role.description ||
                              `Perfil ${role.name}`
                            }
                          >
                            <Box
                              sx={{
                                mx: 'auto',
                                display: 'inline-block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: isMobile
                                  ? 'nowrap'
                                  : 'normal',
                                writingMode: isMobile
                                  ? 'horizontal-tb'
                                  : 'vertical-rl',
                                transform: isMobile
                                  ? 'none'
                                  : 'rotate(180deg)',
                                lineHeight: 1.1,
                                fontSize: {
                                  xs: '0.70rem',
                                  md: '0.74rem',
                                },
                              }}
                            >
                              {role.name}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleMatrixPermissions.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3 + matrix.roles.length}
                          align="center"
                          sx={{ py: 6 }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            No hay permisos que coincidan con los filtros.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleMatrixPermissions.map(
                        (permission, index) => {
                          const previous =
                            visibleMatrixPermissions[index - 1];

                          const showModuleHeader =
                            !previous ||
                            getPermissionModuleKey(previous) !==
                              getPermissionModuleKey(permission);

                          const permissionId =
                            getPermissionId(permission);

                          return (
                            <React.Fragment key={permissionId}>
                              {showModuleHeader && (
                                <TableRow>
                                  <TableCell
                                    colSpan={3 + matrix.roles.length}
                                    sx={{
                                      py: 0.7,
                                      px: 1.5,
                                      bgcolor: 'action.hover',
                                      color: 'primary.main',
                                      fontWeight: 900,
                                      letterSpacing: 0.35,
                                    }}
                                  >
                                    {permission.module_name || 'General'}
                                  </TableCell>
                                </TableRow>
                              )}

                              <TableRow hover>
                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    width: moduleWidth,
                                    minWidth: moduleWidth,
                                    maxWidth: moduleWidth,
                                    bgcolor: 'background.paper',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    py: 0.8,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    noWrap
                                  >
                                    {permission.module_name || 'General'}
                                  </Typography>
                                </TableCell>

                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: resourceLeft,
                                    zIndex: 2,
                                    width: resourceWidth,
                                    minWidth: resourceWidth,
                                    maxWidth: resourceWidth,
                                    bgcolor: 'background.paper',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    py: 0.8,
                                  }}
                                >
                                  <Tooltip
                                    title={permission.name}
                                    placement="top"
                                    arrow
                                  >
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        noWrap
                                      >
                                        {permission.resource_name ||
                                          'General'}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                        sx={{
                                          display: 'block',
                                          fontFamily: 'monospace',
                                          fontSize: '0.64rem',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {permission.name}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </TableCell>

                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: actionLeft,
                                    zIndex: 2,
                                    width: actionWidth,
                                    minWidth: actionWidth,
                                    maxWidth: actionWidth,
                                    bgcolor: 'background.paper',
                                    borderRight: '1px solid',
                                    borderColor: 'divider',
                                    py: 0.8,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={
                                      permission.action_name || 'Ver'
                                    }
                                    sx={{
                                      maxWidth: '100%',
                                      '& .MuiChip-label': {
                                        px: 0.75,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      },
                                    }}
                                  />
                                </TableCell>

                                {matrix.roles.map((role) => {
                                  const roleId = getRoleId(role);

                                  const checked = normalizeIds(
                                    permission.role_ids
                                  ).includes(roleId);

                                  const cellKey = `${permissionId}:${roleId}`;
                                  const isSaving =
                                    pendingCells.has(cellKey);

                                  return (
                                    <TableCell
                                      key={cellKey}
                                      align="center"
                                      sx={{
                                        width: matrixRoleColumnWidth,
                                        minWidth: matrixRoleColumnWidth,
                                        maxWidth: matrixRoleColumnWidth,
                                        px: 0,
                                        py: 0.3,
                                      }}
                                    >
                                      <Tooltip
                                        arrow
                                        title={
                                          checked
                                            ? `Retirar acceso a ${role.name}`
                                            : `Otorgar acceso a ${role.name}`
                                        }
                                      >
                                        <span>
                                          <Checkbox
                                            size="small"
                                            checked={checked}
                                            disabled={isSaving}
                                            color="primary"
                                            sx={{ p: 0.45 }}
                                            onChange={(event) =>
                                              updatePermissionRole(
                                                permissionId,
                                                roleId,
                                                event.target.checked
                                              )
                                            }
                                          />
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            </React.Fragment>
                          );
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  px: 2,
                  py: 1.1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Mostrando {visibleMatrixPermissions.length} de{' '}
                  {matrix.permissions.length} permisos ·{' '}
                  {matrix.roles.length} perfiles disponibles.
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* ────────────────────────────── CATÁLOGO ────────────────────────────── */}

      {activeTab === 'permissions' && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              minWidth: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', xl: 'center' }}
              sx={{ minWidth: 0 }}
            >
              <Box minWidth={0}>
                <Typography variant="subtitle1" fontWeight={900}>
                  Catálogo de permisos
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  Los códigos técnicos se utilizan en rutas, botones,
                  Navbar, API y validaciones.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {renderSearchField(
                  catalogSearch,
                  setCatalogSearch,
                  'Buscar en catálogo…'
                )}

                {renderModuleSelect(
                  catalogModuleFilter,
                  setCatalogModuleFilter
                )}
              </Stack>
            </Stack>
          </Box>

          {matrixLoading ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  width: '100%',
                  minWidth: 850,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell width={72}>ID</TableCell>
                    <TableCell width="27%">Permiso</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell width="25%">Perfiles asignados</TableCell>
                    <TableCell align="right" width={120}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleCatalogPermissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          No se encontraron permisos.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleCatalogPermissions.map((permission) => {
                      const assignedRoleIds = normalizeIds(
                        permission.role_ids
                      );

                      const assignedRoles = availableRoles.filter((role) =>
                        assignedRoleIds.includes(getRoleId(role))
                      );

                      return (
                        <TableRow
                          key={getPermissionId(permission)}
                          hover
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`#${permission.id}`}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={800}
                            >
                              {permissionDisplayName(permission)}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mt: 0.3,
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {permission.name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {permission.description || '—'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {assignedRoles.length > 0 ? (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {assignedRoles.slice(0, 5).map((role) => (
                                  <Chip
                                    key={getRoleId(role)}
                                    size="small"
                                    label={role.name}
                                    color="primary"
                                    variant="outlined"
                                  />
                                ))}

                                {assignedRoles.length > 5 && (
                                  <Tooltip
                                    title={assignedRoles
                                      .slice(5)
                                      .map((role) => role.name)
                                      .join(', ')}
                                    arrow
                                  >
                                    <Chip
                                      size="small"
                                      label={`+${assignedRoles.length - 5}`}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sin perfiles asignados
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell align="right">
                            <Tooltip title="Editar permiso">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  openEditPermission(permission)
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar permiso">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  requestDeletePermission(permission)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ────────────────────────────── ROLES ────────────────────────────── */}

      {activeTab === 'roles' && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              minWidth: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', xl: 'center' }}
              sx={{ minWidth: 0 }}
            >
              <Box minWidth={0}>
                <Typography variant="subtitle1" fontWeight={900}>
                  Perfiles de acceso
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  Cada perfil agrupa permisos reutilizables para usuarios
                  y empresas.
                </Typography>
              </Box>

              {renderSearchField(
                rolesSearch,
                setRolesSearch,
                'Buscar perfil…'
              )}
            </Stack>
          </Box>

          {rolesLoading ? (
            <Box sx={{ py: 7, textAlign: 'center' }}>
              <CircularProgress />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1.5 }}
              >
                Cargando perfiles…
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  width: '100%',
                  minWidth: 720,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell width={72}>ID</TableCell>
                    <TableCell width="28%">Perfil</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center" width={140}>
                      Permisos
                    </TableCell>
                    <TableCell align="right" width={120}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRoles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          No se encontraron perfiles.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRoles.map((role) => (
                      <TableRow key={getRoleId(role)} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`#${getRoleId(role)}`}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <ManageAccountsIcon
                              color="primary"
                              fontSize="small"
                            />

                            <Typography
                              variant="body2"
                              fontWeight={800}
                            >
                              {role.name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {role.description || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            size="small"
                            color={
                              Number(role.permission_count || 0) > 0
                                ? 'success'
                                : 'default'
                            }
                            label={`${Number(
                              role.permission_count || 0
                            )} permisos`}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title="Editar perfil y permisos">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditRole(role)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar perfil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                requestDeleteRole(role)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ────────────────────────────── DIALOG PERMISO ────────────────────────────── */}

      <Dialog
        open={permissionDialog.open}
        onClose={closePermissionDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <VpnKeyIcon color="primary" />

            <Box>
              <Typography variant="h6" fontWeight={900}>
                {permissionDialog.mode === 'edit'
                  ? 'Editar permiso'
                  : 'Nuevo permiso'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Define el código técnico y los perfiles autorizados.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={closePermissionDialog}
            disabled={permissionDialog.saving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label="Código técnico"
              value={permissionDialog.form.name}
              disabled={permissionDialog.saving}
              onChange={(event) =>
                updatePermissionForm('name', event.target.value)
              }
              onBlur={() =>
                updatePermissionForm(
                  'name',
                  normalizePermissionCode(permissionDialog.form.name)
                )
              }
              helperText="Formato: módulo:submódulo:acción. Ejemplo: contabilidad:plan-cuentas:ver"
              inputProps={{
                autoCapitalize: 'none',
                autoCorrect: 'off',
                spellCheck: false,
              }}
            />

            {permissionCodeChanged && (
              <Alert severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={800}>
                    Cambio de código técnico
                  </Typography>

                  <Typography variant="caption">
                    Este código puede estar utilizado en rutas, Navbar,
                    API y botones internos. Confirma que actualizarás sus
                    referencias.
                  </Typography>

                  <Box>
                    <Checkbox
                      checked={
                        permissionDialog.form.confirmCodeChange
                      }
                      disabled={permissionDialog.saving}
                      onChange={(event) =>
                        updatePermissionForm(
                          'confirmCodeChange',
                          event.target.checked
                        )
                      }
                    />

                    <Typography
                      component="span"
                      variant="body2"
                    >
                      Confirmo el cambio del código técnico.
                    </Typography>
                  </Box>
                </Stack>
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descripción"
              value={permissionDialog.form.description}
              disabled={permissionDialog.saving}
              onChange={(event) =>
                updatePermissionForm(
                  'description',
                  event.target.value
                )
              }
              placeholder="Describe claramente qué permite hacer este acceso."
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={900} mb={0.5}>
                Perfiles con este permiso
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1.25}
              >
                Puedes asignarlo a uno o más perfiles. Después también
                podrás administrarlo desde la matriz.
              </Typography>

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={availableRoles}
                value={selectedPermissionRoles}
                disabled={permissionDialog.saving}
                getOptionLabel={(role) => role.name || ''}
                isOptionEqualToValue={(option, value) =>
                  getRoleId(option) === getRoleId(value)
                }
                onChange={(_, selected) =>
                  updatePermissionForm(
                    'role_ids',
                    normalizeIds(
                      selected.map((role) => getRoleId(role))
                    )
                  )
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={getRoleId(option)}>
                    <Checkbox
                      size="small"
                      checked={selected}
                      sx={{ mr: 1 }}
                    />

                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {option.name}
                      </Typography>

                      {option.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Seleccionar perfiles"
                    placeholder="Buscar perfil…"
                  />
                )}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closePermissionDialog}
            disabled={permissionDialog.saving}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={
              permissionDialog.saving ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={savePermission}
            disabled={permissionDialog.saving}
          >
            {permissionDialog.mode === 'edit'
              ? 'Guardar cambios'
              : 'Crear permiso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────── DIALOG ROL ────────────────────────────── */}

      <Dialog
        open={roleDialog.open}
        onClose={closeRoleDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ManageAccountsIcon color="primary" />

            <Box>
              <Typography variant="h6" fontWeight={900}>
                {roleDialog.mode === 'edit'
                  ? 'Editar perfil'
                  : 'Nuevo perfil'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Agrupa permisos reutilizables para usuarios y empresas.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={closeRoleDialog}
            disabled={roleDialog.saving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {roleDialog.loading ? (
            <Box
              sx={{
                minHeight: 240,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress />

                <Typography variant="body2" color="text.secondary">
                  Cargando permisos del perfil…
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack spacing={2}>
              <TextField
                required
                fullWidth
                label="Nombre del perfil"
                value={roleDialog.form.name}
                disabled={roleDialog.saving}
                onChange={(event) =>
                  updateRoleForm('name', event.target.value)
                }
                placeholder="Ejemplo: Contador"
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Descripción"
                value={roleDialog.form.description}
                disabled={roleDialog.saving}
                onChange={(event) =>
                  updateRoleForm(
                    'description',
                    event.target.value
                  )
                }
                placeholder="Describe el propósito de este perfil."
              />

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={900} mb={0.5}>
                  Permisos del perfil
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={1.25}
                >
                  Selecciona los permisos que tendrá este perfil. También
                  puedes modificarlo directamente desde la matriz.
                </Typography>

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  groupBy={(permission) =>
                    permission.module_name || 'General'
                  }
                  options={matrix.permissions}
                  value={selectedRolePermissions}
                  disabled={roleDialog.saving}
                  getOptionLabel={(permission) =>
                    `${permissionDisplayName(permission)} · ${permission.name}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    getPermissionId(option) === getPermissionId(value)
                  }
                  onChange={(_, selected) =>
                    updateRoleForm(
                      'permission_ids',
                      normalizeIds(
                        selected.map((permission) =>
                          getPermissionId(permission)
                        )
                      )
                    )
                  }
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color="primary"
                        sx={{
                          display: 'block',
                          px: 2,
                          py: 0.8,
                          bgcolor: 'action.hover',
                        }}
                      >
                        {params.group}
                      </Typography>

                      <ul
                        style={{
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        {params.children}
                      </ul>
                    </li>
                  )}
                  renderOption={(props, permission, { selected }) => (
                    <li {...props} key={getPermissionId(permission)}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1 }}
                      />

                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {permissionDisplayName(permission)}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            fontFamily: 'monospace',
                          }}
                        >
                          {permission.name}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar permisos"
                      placeholder="Buscar permiso…"
                    />
                  )}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                >
                  {normalizeIds(roleDialog.form.permission_ids).length}{' '}
                  permisos seleccionados.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeRoleDialog}
            disabled={roleDialog.saving}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            startIcon={
              roleDialog.saving ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={saveRole}
            disabled={roleDialog.saving || roleDialog.loading}
          >
            {roleDialog.mode === 'edit'
              ? 'Guardar cambios'
              : 'Crear perfil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────── DIALOG ELIMINAR ────────────────────────────── */}

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon color="warning" />

            <Typography variant="h6" fontWeight={900}>
              {deleteDialog.forceRequired
                ? 'Registro con relaciones activas'
                : 'Confirmar eliminación'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5}>
            {!deleteDialog.forceRequired ? (
              <Typography variant="body2">
                ¿Deseas eliminar{' '}
                <strong>{deleteDialog.item?.name}</strong>?
              </Typography>
            ) : (
              <>
                <Typography variant="body2">
                  {deleteDialog.conflictMessage}
                </Typography>

                <Alert severity="warning">
                  Al forzar la eliminación también se eliminarán sus
                  asignaciones relacionadas.
                </Alert>
              </>
            )}

            <Typography variant="caption" color="text.secondary">
              Esta acción no se puede deshacer.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleteDialog.deleting}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={deleteDialog.deleting}
            startIcon={
              deleteDialog.deleting ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            onClick={() =>
              confirmDelete({
                force: deleteDialog.forceRequired,
              })
            }
          >
            {deleteDialog.forceRequired
              ? 'Forzar eliminación'
              : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────── SNACKBAR ────────────────────────────── */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}